import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, FolderKanban, Layers3, LogOut, Menu, X, ListTodo, BarChart3, Shield, PauseCircle, UserPlus } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";
import { overviewLabelSr } from "@/lib/api";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/", label: overviewLabelSr(), icon: LayoutDashboard, testid: "nav-jutarnji" },
    { to: "/taskovi", label: "Zadaci", icon: ListTodo, testid: "nav-taskovi" },
    { to: "/cekam", label: "Čekam", icon: PauseCircle, testid: "nav-cekam" },
    { to: "/saradnici", label: "Saradnici", icon: Users, testid: "nav-saradnici" },
    { to: "/projekti", label: "Projekti", icon: FolderKanban, testid: "nav-projekti" },
    { to: "/grupacije", label: "Grupacije", icon: Layers3, testid: "nav-grupacije" },
  ];
  const adminNav = [
    { to: "/analitika", label: "Analitika", icon: BarChart3, testid: "nav-analitika" },
    { to: "/zahtevi", label: "Zahtevi za registraciju", icon: UserPlus, testid: "nav-zahtevi" },
  ];

  const onLogout = () => { logout(); navigate("/login"); };
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex bg-background text-ink">
      <aside
        data-testid="sidebar"
        className={`fixed md:static inset-y-0 left-0 z-30 w-64 bg-surface-secondary border-r border-line
        transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md bg-forest grid place-items-center text-white font-heading font-bold">V</div>
            <div>
              <div className="font-heading font-semibold text-ink leading-tight">Voden</div>
              <div className="text-xs text-ink-muted -mt-0.5">Project Management</div>
            </div>
          </Link>
          <button className="md:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="px-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-forest text-white" : "text-ink hover:bg-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="pt-4 pb-1 px-3 overline">Administracija</div>
              {adminNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive ? "bg-forest text-white" : "text-ink hover:bg-white"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-line bg-surface-secondary">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/profil"
              onClick={() => setOpen(false)}
              className="min-w-0 flex-1 hover:opacity-80 transition-opacity"
              data-testid="profile-link"
              title="Uredi profil"
            >
              <div className="text-sm font-medium truncate flex items-center gap-1.5" data-testid="user-name">
                {user?.name}
                {isAdmin && <Shield className="w-3 h-3 text-forest" />}
              </div>
              <div className="text-xs text-ink-muted truncate">{user?.email}</div>
            </Link>
            <button onClick={onLogout} data-testid="logout-btn" className="btn-ghost !px-2 !py-2" title="Odjava">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 bg-background border-b border-line">
          <button data-testid="open-sidebar" onClick={() => setOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-heading font-semibold">Voden</div>
          <div className="md:hidden"><NotificationBell /></div>
        </header>
        <div className="hidden md:flex sticky top-0 z-20 items-center justify-end px-8 lg:px-12 py-3 bg-background border-b border-line">
          <NotificationBell />
        </div>
        <div className="px-4 md:px-8 lg:px-12 py-6 md:py-10 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
